import { useEffect } from "react";

/**
 * Captura dos leads do widget do Bitrix no /contato (24/09/2026).
 *
 * O widget continua sendo quem entrega ao Bitrix — nada nele muda. Este ouvinte
 * só pega os valores no instante em que o widget vai enviar e manda uma cópia
 * para a edge function `lead-site`, que grava o lead no nosso banco (contato +
 * card "Assessoria" no kanban, visível para super admin).
 *
 * O evento foi medido no widget de verdade, com a rede bloqueada: com o
 * formulário válido ele dispara `b24:form:submit`, `b24:form:submit:post:before`
 * (logo antes do POST ao Bitrix) e `b24:form:send:success|error`. Com o
 * formulário inválido, nenhum. `event.detail.object.values()` devolve cada
 * campo como lista, e as escolhas como o id da opção no Bitrix.
 *
 * Se a captura falhar (rede, function fora do ar, Bitrix mudar o evento), o
 * lead continua chegando ao Bitrix: a cópia é um extra, nunca um bloqueio.
 */

export const EVENTO_ENVIO_DO_WIDGET = "b24:form:submit:post:before";

type Valores = Record<string, unknown>;

const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** De onde a pessoa veio: UTMs do link (query do hash ou da página) e referrer. */
export function origemDaVisita(hash: URLSearchParams, pagina: URLSearchParams, referrer: string) {
  const origem: Record<string, string> = {};
  for (const k of UTM) {
    const v = hash.get(k) ?? pagina.get(k);
    if (v) origem[k] = v;
  }
  if (referrer) origem.referrer = referrer;
  origem.pagina = "/contato";
  return origem;
}

/** Os valores do widget a partir do evento; null se o formato não for o esperado. */
export function valoresDoEvento(ev: Event): Valores | null {
  const objeto = (ev as CustomEvent)?.detail?.object;
  if (!objeto || typeof objeto.values !== "function") return null;
  try {
    const v = objeto.values();
    return v && typeof v === "object" ? (v as Valores) : null;
  } catch {
    return null;
  }
}

/**
 * Manda a cópia. `keepalive`: logo depois do envio o Bitrix redireciona para
 * ?obrigado=1, e sem isso o navegador cancelaria a requisição no meio.
 */
export function enviarCopia(valores: Valores, origem: Record<string, string>, fetchImpl: typeof fetch = fetch) {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anon) return Promise.resolve();
  return fetchImpl(`${url}/functions/v1/lead-site`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", apikey: anon, Authorization: `Bearer ${anon}` },
    body: JSON.stringify({ widget: { valores }, origem }),
  })
    .then(() => undefined)
    .catch(() => undefined);
}

/**
 * Liga o ouvinte enquanto o /contato está aberto. O mesmo envio pode disparar
 * o evento mais de uma vez (a pessoa clica de novo depois de um erro do
 * Bitrix); a cópia idêntica dentro de um minuto é ignorada aqui, e a function
 * ainda descarta o mesmo e-mail em 10 minutos.
 */
export function useCapturaDoWidget(origem: Record<string, string>, fetchImpl: typeof fetch = fetch) {
  useEffect(() => {
    let ultimo = { chave: "", quando: 0 };
    const aoEnviar = (ev: Event) => {
      const valores = valoresDoEvento(ev);
      if (!valores) return;
      const chave = JSON.stringify(valores);
      const agora = Date.now();
      if (chave === ultimo.chave && agora - ultimo.quando < 60_000) return;
      ultimo = { chave, quando: agora };
      void enviarCopia(valores, origem, fetchImpl);
    };
    window.addEventListener(EVENTO_ENVIO_DO_WIDGET, aoEnviar);
    return () => window.removeEventListener(EVENTO_ENVIO_DO_WIDGET, aoEnviar);
  }, [origem, fetchImpl]);
}
